package shared.utils

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.js.Promise

object ImageUtils {
    /**
     * Resize image to specified dimensions and return base64 string
     * @param file The image file to resize
     * @param maxWidth Maximum width (default 100)
     * @param maxHeight Maximum height (default 100)
     * @param quality JPEG quality 0-1 (default 0.8)
     * @return Promise with base64 string (without data:image prefix)
     */
    fun resizeImage(
        file: File, 
        maxWidth: Int = 100, 
        maxHeight: Int = 100,
        quality: Double = 0.8
    ): Promise<String> {
        return Promise { resolve, reject ->
            // Check file size (max 5MB)
            val maxSize = 5 * 1024 * 1024
            if (file.size.toLong() > maxSize) {
                reject(Exception("File size too large. Maximum 5MB allowed."))
                return@Promise
            }
            
            // Check file type
            if (!file.type.startsWith("image/")) {
                reject(Exception("Invalid file type. Please select an image."))
                return@Promise
            }
            
            val reader = FileReader()
            
            reader.onload = { event ->
                try {
                    val dataUrl = event.target.asDynamic().result as String
                    
                    // Create image element
                    val img = document.createElement("img") as HTMLImageElement
                    
                    img.onload = {
                        try {
                            // Calculate new dimensions maintaining aspect ratio
                            var width = img.width
                            var height = img.height
                            
                            if (width > height) {
                                if (width > maxWidth) {
                                    height = (height * maxWidth / width)
                                    width = maxWidth
                                }
                            } else {
                                if (height > maxHeight) {
                                    width = (width * maxHeight / height)
                                    height = maxHeight
                                }
                            }
                            
                            // Create canvas
                            val canvas = document.createElement("canvas") as HTMLCanvasElement
                            canvas.width = width
                            canvas.height = height
                            
                            // Draw resized image
                            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
                            ctx.asDynamic().drawImage(img, 0, 0, width, height)
                            
                            // Convert to base64
                            val mimeType = if (file.type == "image/png") "image/png" else "image/jpeg"
                            val qualityParam = if (mimeType == "image/jpeg") quality else null
                            val base64 = if (qualityParam != null) {
                                canvas.asDynamic().toDataURL(mimeType, qualityParam) as String
                            } else {
                                canvas.asDynamic().toDataURL(mimeType) as String
                            }
                            
                            // Remove data URL prefix (data:image/jpeg;base64,)
                            val base64Data = base64.substringAfter("base64,")
                            
                            console.log("✅ Image resized: ${width}x${height}, size: ${base64Data.length} chars")
                            resolve(base64Data)
                        } catch (e: dynamic) {
                            console.error("Error resizing image:", e)
                            reject(Exception("Failed to resize image: ${e.message}"))
                        }
                    }
                    
                    img.onerror = { _, _, _, _, _ ->
                        reject(Exception("Failed to load image"))
                    }
                    
                    img.src = dataUrl
                } catch (e: dynamic) {
                    console.error("Error reading file:", e)
                    reject(Exception("Failed to read file: ${e.message}"))
                }
            }
            
            reader.onerror = {
                reject(Exception("Failed to read file"))
            }
            
            reader.readAsDataURL(file)
        }
    }
    
    /**
     * Get data URL from base64 string
     */
    fun getDataUrl(base64: String, mimeType: String = "image/jpeg"): String {
        return "data:$mimeType;base64,$base64"
    }
}
